import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H1, Separator, Spinner } from '@betterment/samba-react';

import { EmergencyFundProjection } from './emergency-fund/EmergencyFundProjection';
import { HeaderContent } from './HeaderContent';
import { MajorPurchaseProjection } from './major-purchase/MajorPurchaseProjection';
import { RetirementGrowthProjection } from './retirement-growth/RetirementGrowthProjection';
import { RetirementIncomeProjection } from './retirement-income/RetirementIncomeProjection';

import { MinimalFeeWarning } from '../../../../Shared/components-v2/minimal-fee-warning/MinimalFeeWarning';
import { ForecasterProjectionLoadingState } from '../../../shared/components/forecaster-projection-loading-state/ForecasterProjectionLoadingState';
import { ForecasterProjectionDisclosuresDocument } from '../../../shared/graphql/forecasterProjectionDisclosures.generated';
import { useForecasterPurposeProjection } from '../../context/ForecasterPurposeProjectionContext';
import { Disclosures, Purpose } from '../../data';

const CardContent: React.FC<{ purpose: Purpose; disclosures: Disclosures }> = ({ purpose, disclosures }) => {
  switch (purpose.__typename) {
    case 'PurposeMajorPurchase':
      return <MajorPurchaseProjection purpose={purpose} disclosures={disclosures} />;
    case 'PurposeEmergencyFund':
      return <EmergencyFundProjection purpose={purpose} disclosures={disclosures} />;
    case 'PurposeRetirementGrowth':
      return <RetirementGrowthProjection purpose={purpose} disclosures={disclosures} />;
    case 'PurposeRetirementIncome':
      return <RetirementIncomeProjection purpose={purpose} disclosures={disclosures} />;
    default:
      return purpose;
  }
};

export const PurposeProjectionCard = () => {
  const { purpose, isLoading } = useForecasterPurposeProjection();
  const { data: disclosuresData } = useGraphQLQuery(ForecasterProjectionDisclosuresDocument, {
    throwOnError: true,
  });
  const { disclosures } = disclosuresData || {};

  if (!purpose || !disclosures)
    return <ForecasterProjectionLoadingState withBalances={true} withHeaderContent={true} />;

  const managedAccount = purpose.envelope?.accounts?.find(account => account.__typename === 'ManagedAccount');

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300', position: 'relative' }}>
      {isLoading && (
        <Box
          css={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
            backgroundColor: 'primitives.white.100alpha30',
          }}
        >
          <Spinner />
        </Box>
      )}
      <Card.Header css={{ display: 'flex', flexDirection: 'column', gap: 'space.100' }}>
        <H1 variant="title100">Balance projection</H1>
        <HeaderContent purpose={purpose} />
      </Card.Header>
      <Separator />
      {managedAccount?.minFeeWarning && <MinimalFeeWarning minFeeWarning={managedAccount.minFeeWarning} />}
      <Card.Content>
        <CardContent purpose={purpose} disclosures={disclosures} />
      </Card.Content>
    </Card>
  );
};
